import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { getRecetasModuleContext } from '../../../utils/recetasModuleContext';
import {
  RECETA_OUTPUT_DEFAULTS,
  RECETA_OUTPUT_PRODUCT_TYPES,
  RECETA_OUTPUT_SALIDA_INSUMO,
  recetaOutputTypesForCoProduct,
  recetaOutputTypesForPrimary,
  RECETA_PEP_USO_EN_PT_OPTIONS,
  UNIDADES_SALIDA_RECETA,
} from '../../../data/mockRecetasClassification';
import { useModuleSettings } from '../../../contexts/ModuleSettingsContext';
import { useRecetaClassification } from '../../../contexts/RecetaClassificationContext';
import recetaPlatoService from '../../../services/recetaPlatoService';
import produccionLineaService from '../../../services/produccionLineaService';
import { getUnits } from '../../../services/unitsService';
import Toast from '../../../components/Common/Toast';
import ProductoBuscador from '../../../components/ProduccionConcesionaria/ProductoBuscador';
import {
  resolveRecipeIngredientUnitCode,
  resolveRecipeIngredientUnitDisplay,
} from '../../../utils/unitSelectUtils';
import {
  RECETA_MAX_DEC_DIGITS,
  RECETA_QTY_DECIMAL_FACTOR,
  RECETA_QTY_MAX,
} from '../../../utils/recipeQuantityLimits';

const newLineId = () => `ln-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const createEmptyIngredientLine = () => ({
  id: newLineId(),
  product_id: null,
  product_label: '',
  quantity: '',
  merma_pct: '0',
  byproduct_open: false,
  byproduct_product_id: null,
  byproduct_label: '',
  byproduct_pct: '0',
  byproduct_unit: '',
  unit: '',
});

/** Cantidad: solo números y un punto decimal; máx. 8 decimales (parte entera sin límite artificial). */
function sanitizeQuantityInput(value) {
  if (value === '') return '';
  const s = String(value).replace(/[^\d.]/g, '');
  const dotIdx = s.indexOf('.');
  const intPart = dotIdx === -1 ? s : s.slice(0, dotIdx);
  if (dotIdx === -1) {
    return intPart;
  }
  let decPart = s.slice(dotIdx + 1).replace(/\./g, '');
  if (decPart.length > RECETA_MAX_DEC_DIGITS) {
    decPart = decPart.slice(0, RECETA_MAX_DEC_DIGITS);
  }
  return intPart + '.' + decPart;
}

/**
 * Alta / edición de receta (API /recetas/platos). Ingredientes: solo productos insumo.
 */
const RecetasForm = () => {
  const location = useLocation();
  const { moduleBase, moduleName } = getRecetasModuleContext(location.pathname);
  const { produccionEnabled, produccionLineasEnabled, produccionFabricarPepDesdePtEnabled } = useModuleSettings();
  /** Configuración de salida: solo módulo Producción general (/produccion), no Restaurantes ni concesionaria. */
  const showOutputConfig = produccionEnabled && moduleBase === '/produccion';
  const showLineaField = produccionLineasEnabled && moduleBase === '/produccion';
  const listPath = `${moduleBase}/clasificacion-recetas/recetas`;
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);

  const { hierarchy, loading: loadingClasificacion, insumoProducts } = useRecetaClassification();

  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [subcategoryId, setSubcategoryId] = useState('');
  const [familyId, setFamilyId] = useState('');
  const [produccionLineaId, setProduccionLineaId] = useState('');
  const [lineas, setLineas] = useState([]);
  const [active, setActive] = useState(true);
  const [outputProductType, setOutputProductType] = useState(RECETA_OUTPUT_DEFAULTS.output_product_type);
  const [outputQuantity, setOutputQuantity] = useState(RECETA_OUTPUT_DEFAULTS.output_quantity);
  const [outputUnit, setOutputUnit] = useState(RECETA_OUTPUT_DEFAULTS.output_unit_of_measure);
  const [fabricarSubrecetaDesdePt, setFabricarSubrecetaDesdePt] = useState(
    produccionFabricarPepDesdePtEnabled,
  );
  const [multiOutputEnabled, setMultiOutputEnabled] = useState(false);
  const [outputs, setOutputs] = useState(() => [
    {
      id: null,
      localId: newLineId(),
      name: '',
      product_id: null,
      output_product_type: RECETA_OUTPUT_DEFAULTS.output_product_type,
      output_quantity: RECETA_OUTPUT_DEFAULTS.output_quantity,
      output_unit_of_measure: RECETA_OUTPUT_DEFAULTS.output_unit_of_measure,
      cost_share_pct: '50',
    },
    {
      id: null,
      localId: newLineId(),
      name: '',
      product_id: null,
      output_product_type: RECETA_OUTPUT_DEFAULTS.output_product_type,
      output_quantity: RECETA_OUTPUT_DEFAULTS.output_quantity,
      output_unit_of_measure: RECETA_OUTPUT_DEFAULTS.output_unit_of_measure,
      cost_share_pct: '50',
    },
  ]);
  const [outputUnitsOptions, setOutputUnitsOptions] = useState(UNIDADES_SALIDA_RECETA);
  const [ingredientUnits, setIngredientUnits] = useState([]);
  const [lines, setLines] = useState(() => [createEmptyIngredientLine()]);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [loadingRecipe, setLoadingRecipe] = useState(false);
  const [saving, setSaving] = useState(false);

  const subcategories = useMemo(() => {
    if (!categoryId) return [];
    const cat = hierarchy.find((c) => String(c.id) === String(categoryId));
    return (cat?.subcategories || []).filter((s) => s.is_active);
  }, [categoryId, hierarchy]);

  const families = useMemo(() => {
    if (!subcategoryId) return [];
    for (const cat of hierarchy) {
      const sub = cat.subcategories?.find((s) => String(s.id) === String(subcategoryId));
      if (sub) return (sub.families || []).filter((f) => f.is_active);
    }
    return [];
  }, [subcategoryId, hierarchy]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getUnits({ is_active: true });
        if (cancelled || !res.success || !Array.isArray(res.data)) return;
        setIngredientUnits(res.data);
        if (!showOutputConfig) return;
        const fromApi = res.data
          .map((u) => (u.symbol || u.code || u.name || '').trim())
          .filter(Boolean);
        const merged = [...new Set([...UNIDADES_SALIDA_RECETA, ...fromApi])];
        setOutputUnitsOptions(merged);
      } catch {
        /* mantener lista local */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showOutputConfig]);

  useEffect(() => {
    if (!showLineaField) return;
    produccionLineaService.list({ active_only: true }).then((res) => {
      if (res.success) setLineas(res.data || []);
    }).catch(() => {});
  }, [showLineaField]);

  const pickOutputUnit = (raw) => {
    if (raw == null || String(raw).trim() === '') return RECETA_OUTPUT_DEFAULTS.output_unit_of_measure;
    const s = String(raw).trim();
    const found = outputUnitsOptions.find((u) => u.toLowerCase() === s.toLowerCase());
    return found || s;
  };

  const classificationPreview = useMemo(() => {
    if (!categoryId || !subcategoryId || !familyId) return '—';
    const cat = hierarchy.find((c) => String(c.id) === String(categoryId));
    const sub = cat?.subcategories?.find((s) => String(s.id) === String(subcategoryId));
    const fam = sub?.families?.find((f) => String(f.id) === String(familyId));
    if (!cat || !sub || !fam) return '—';
    return `${cat.name} › ${sub.name} › ${fam.name}`;
  }, [categoryId, subcategoryId, familyId, hierarchy]);

  const loadRecipe = useCallback(async () => {
    if (!isEdit || !editId) return;
    setLoadingRecipe(true);
    try {
      const res = await recetaPlatoService.get(editId);
      if (!res.success || !res.data) {
        setToast({ isOpen: true, message: res.message || 'No se encontró la receta', type: 'error' });
        return;
      }
      const d = res.data;
      setName(d.name || '');
      setActive(d.is_active !== false);
      if (showLineaField) {
        setProduccionLineaId(d.produccion_linea_id != null ? String(d.produccion_linea_id) : '');
      }
      if (showOutputConfig) {
        setOutputProductType(d.output_product_type || RECETA_OUTPUT_DEFAULTS.output_product_type);
        setOutputQuantity(
          d.output_quantity != null ? String(d.output_quantity) : RECETA_OUTPUT_DEFAULTS.output_quantity,
        );
        setOutputUnit(pickOutputUnit(d.output_unit_of_measure ?? RECETA_OUTPUT_DEFAULTS.output_unit_of_measure));
        if (d.output_product_type === 'producto_en_proceso') {
          setFabricarSubrecetaDesdePt(
            d.fabricar_subreceta_desde_pt !== null && d.fabricar_subreceta_desde_pt !== undefined
              ? Boolean(d.fabricar_subreceta_desde_pt)
              : produccionFabricarPepDesdePtEnabled,
          );
        }
        const multi = Boolean(d.multi_output_enabled);
        setMultiOutputEnabled(multi);
        if (multi && Array.isArray(d.outputs) && d.outputs.length >= 2) {
          setOutputs(
            d.outputs.map((o) => ({
              id: o.id ?? null,
              localId: newLineId(),
              name: o.name || o.finished_product?.name || '',
              product_id: o.product_id ?? o.finished_product?.id ?? null,
              output_product_type: o.output_product_type || RECETA_OUTPUT_DEFAULTS.output_product_type,
              output_quantity:
                o.output_quantity != null ? String(o.output_quantity) : RECETA_OUTPUT_DEFAULTS.output_quantity,
              output_unit_of_measure: pickOutputUnit(
                o.output_unit_of_measure ?? RECETA_OUTPUT_DEFAULTS.output_unit_of_measure,
              ),
              cost_share_pct: o.cost_share_pct != null ? String(o.cost_share_pct) : '',
            })),
          );
        }
      }

      let foundCat = '';
      let foundSub = '';
      let foundFam = '';
      for (const c of hierarchy) {
        for (const s of c.subcategories || []) {
          const f = (s.families || []).find((x) => String(x.id) === String(d.receta_family_id));
          if (f) {
            foundCat = String(c.id);
            foundSub = String(s.id);
            foundFam = String(f.id);
            break;
          }
        }
        if (foundFam) break;
      }
      setCategoryId(foundCat);
      setSubcategoryId(foundSub);
      setFamilyId(foundFam);

      const itemRows = (d.items || []).map((it) => {
        const bpId = it.byproduct_product_id ?? it.byproduct_product?.id ?? null;
        const bpPct = parseFloat(String(it.byproduct_pct ?? 0).replace(',', '.'));
        const hasByproduct = bpId != null || (Number.isFinite(bpPct) && bpPct > 0);
        return {
          id: newLineId(),
          product_id: it.product_id,
          product_label: it.product?.name || '',
          quantity: it.quantity != null ? String(it.quantity) : '',
          merma_pct: it.merma_pct != null ? String(it.merma_pct) : '0',
          byproduct_open: hasByproduct,
          byproduct_product_id: bpId,
          byproduct_label: it.byproduct_product?.name || '',
          byproduct_pct: it.byproduct_pct != null ? String(it.byproduct_pct) : '0',
          byproduct_unit: resolveRecipeIngredientUnitDisplay(
            it.byproduct_product?.unit_of_measure ?? '',
            ingredientUnits,
          ),
          unit: resolveRecipeIngredientUnitDisplay(
            it.product?.unit_of_measure ?? it.unit_of_measure ?? 'kg',
            ingredientUnits,
          ),
        };
      });
      setLines(itemRows.length ? itemRows : [createEmptyIngredientLine()]);
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'Error al cargar la receta',
        type: 'error',
      });
    } finally {
      setLoadingRecipe(false);
    }
  }, [isEdit, editId, hierarchy, ingredientUnits, outputUnitsOptions, showOutputConfig]);

  useEffect(() => {
    if (!isEdit || !editId || loadingClasificacion) return;
    loadRecipe();
  }, [isEdit, editId, loadingClasificacion, loadRecipe]);

  useEffect(() => {
    if (!categoryId) {
      setSubcategoryId('');
      setFamilyId('');
      return;
    }
    const stillValid = subcategories.some((s) => String(s.id) === String(subcategoryId));
    if (!stillValid) {
      setSubcategoryId('');
      setFamilyId('');
    }
  }, [categoryId, subcategories, subcategoryId]);

  useEffect(() => {
    if (!subcategoryId) {
      setFamilyId('');
      return;
    }
    const stillValid = families.some((f) => String(f.id) === String(familyId));
    if (!stillValid) setFamilyId('');
  }, [subcategoryId, families, familyId]);

  const updateLine = (lineId, patch) => {
    setLines((prev) => prev.map((l) => (l.id === lineId ? { ...l, ...patch } : l)));
  };

  const addLine = () => {
    setLines((prev) => [...prev, createEmptyIngredientLine()]);
  };

  const removeLine = (lineId) => {
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((l) => l.id !== lineId)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setToast({ isOpen: true, message: 'Indique el nombre de la receta', type: 'error' });
      return;
    }
    if (!categoryId || !subcategoryId || !familyId) {
      setToast({ isOpen: true, message: 'Seleccione categoría, subcategoría y familia', type: 'error' });
      return;
    }
    const itemsPayload = [];
    for (const l of lines) {
      if (!l.product_id) continue;
      const qRaw = parseFloat(String(l.quantity).replace(',', '.'));
      if (!Number.isFinite(qRaw) || qRaw <= 0) {
        setToast({ isOpen: true, message: 'Cada ingrediente debe tener cantidad mayor a 0', type: 'error' });
        return;
      }
      const q = Math.round(qRaw * RECETA_QTY_DECIMAL_FACTOR) / RECETA_QTY_DECIMAL_FACTOR;
      if (q > RECETA_QTY_MAX) {
        setToast({
          isOpen: true,
          message: `La cantidad no puede superar ${RECETA_QTY_MAX}`,
          type: 'error',
        });
        return;
      }
      const uom = resolveRecipeIngredientUnitCode(l.unit, ingredientUnits);
      if (!uom) {
        setToast({
          isOpen: true,
          message: 'Cada producto insumo debe tener unidad de medida (proviene de la ficha de producto). Vuelva a elegir el insumo si no aparece.',
          type: 'error',
        });
        return;
      }
      const mermaRaw = parseFloat(String(l.merma_pct ?? '0').replace(',', '.'));
      const mermaPct = Number.isFinite(mermaRaw) && mermaRaw > 0 ? mermaRaw : 0;
      if (mermaPct > 500) {
        setToast({
          isOpen: true,
          message: 'La merma planificada no puede superar 500 %',
          type: 'error',
        });
        return;
      }
      let byproductProductId = null;
      let byproductPct = 0;
      if (showOutputConfig && l.byproduct_open) {
        const bpId = l.byproduct_product_id != null ? Number(l.byproduct_product_id) : null;
        const bpRaw = parseFloat(String(l.byproduct_pct ?? '0').replace(',', '.'));
        const bpPct = Number.isFinite(bpRaw) && bpRaw > 0 ? bpRaw : 0;
        if (bpId && bpPct <= 0) {
          setToast({
            isOpen: true,
            message: 'Si indica subproducto recuperable, el % de recuperación debe ser mayor a 0',
            type: 'error',
          });
          return;
        }
        if (!bpId && bpPct > 0) {
          setToast({
            isOpen: true,
            message: 'Indique el producto del subproducto recuperable o deje el % en 0',
            type: 'error',
          });
          return;
        }
        if (bpId && bpId === Number(l.product_id)) {
          setToast({
            isOpen: true,
            message: 'El subproducto recuperable no puede ser el mismo insumo',
            type: 'error',
          });
          return;
        }
        if (bpPct > 100) {
          setToast({
            isOpen: true,
            message: 'El % de subproducto recuperable no puede superar 100',
            type: 'error',
          });
          return;
        }
        byproductProductId = bpId || null;
        byproductPct = Math.round(bpPct * 10000) / 10000;
      }
      itemsPayload.push({
        product_id: Number(l.product_id),
        quantity: q,
        merma_pct: Math.round(mermaPct * 10000) / 10000,
        ...(showOutputConfig
          ? {
              byproduct_product_id: byproductProductId,
              byproduct_pct: byproductPct,
            }
          : {}),
        unit_of_measure: uom,
      });
    }
    if (itemsPayload.length === 0) {
      setToast({ isOpen: true, message: 'Agregue al menos un producto insumo', type: 'error' });
      return;
    }

    const payload = {
      name: name.trim(),
      receta_family_id: Number(familyId),
      is_active: active,
      items: itemsPayload,
    };

    if (showLineaField) {
      payload.produccion_linea_id = produccionLineaId ? Number(produccionLineaId) : null;
    }

    if (showOutputConfig) {
      payload.multi_output_enabled = multiOutputEnabled;

      if (multiOutputEnabled) {
        if (outputs.length < 2) {
          setToast({
            isOpen: true,
            message: 'Con múltiples salidas debe definir al menos dos productos',
            type: 'error',
          });
          return;
        }
        const outputsPayload = [];
        for (const [idx, out] of outputs.entries()) {
          const outName = idx === 0 ? name.trim() : String(out.name || '').trim();
          if (!outName) {
            setToast({
              isOpen: true,
              message: `Indique el nombre del producto de salida #${idx + 1}`,
              type: 'error',
            });
            return;
          }
          const outQtyRaw = parseFloat(String(out.output_quantity).replace(',', '.'));
          if (!Number.isFinite(outQtyRaw) || outQtyRaw <= 0) {
            setToast({
              isOpen: true,
              message: `La cantidad de «${outName}» debe ser mayor a 0`,
              type: 'error',
            });
            return;
          }
          const outQty = Math.round(outQtyRaw * RECETA_QTY_DECIMAL_FACTOR) / RECETA_QTY_DECIMAL_FACTOR;
          if (outQty > RECETA_QTY_MAX) {
            setToast({
              isOpen: true,
              message: `La cantidad de «${outName}» no puede superar ${RECETA_QTY_MAX}`,
              type: 'error',
            });
            return;
          }
          const outUom = String(out.output_unit_of_measure || '').trim();
          if (!outUom) {
            setToast({ isOpen: true, message: `Seleccione la unidad de «${outName}»`, type: 'error' });
            return;
          }
          const shareRaw = String(out.cost_share_pct || '').trim();
          const share =
            shareRaw === '' ? null : parseFloat(shareRaw.replace(',', '.'));
          if (share != null && (!Number.isFinite(share) || share < 0 || share > 100)) {
            setToast({
              isOpen: true,
              message: `El % de costo de «${outName}» debe estar entre 0 y 100`,
              type: 'error',
            });
            return;
          }
          outputsPayload.push({
            ...(out.id ? { id: out.id } : {}),
            name: outName,
            ...(out.product_id != null ? { product_id: Number(out.product_id) } : {}),
            output_product_type: out.output_product_type || RECETA_OUTPUT_DEFAULTS.output_product_type,
            output_quantity: outQty,
            output_unit_of_measure: outUom,
            ...(share != null ? { cost_share_pct: share } : {}),
          });
        }
        // La cantidad elaborada en guías se escala con la salida primaria (primera fila).
        payload.output_product_type = outputsPayload[0].output_product_type;
        payload.output_quantity = outputsPayload[0].output_quantity;
        payload.output_unit_of_measure = outputsPayload[0].output_unit_of_measure;
        payload.outputs = outputsPayload;
      } else {
        const outQtyRaw = parseFloat(String(outputQuantity).replace(',', '.'));
        if (!Number.isFinite(outQtyRaw) || outQtyRaw <= 0) {
          setToast({ isOpen: true, message: 'La cantidad de salida debe ser mayor a 0', type: 'error' });
          return;
        }
        const outQty = Math.round(outQtyRaw * RECETA_QTY_DECIMAL_FACTOR) / RECETA_QTY_DECIMAL_FACTOR;
        if (outQty > RECETA_QTY_MAX) {
          setToast({
            isOpen: true,
            message: `La cantidad de salida no puede superar ${RECETA_QTY_MAX}`,
            type: 'error',
          });
          return;
        }
        const outUom = String(outputUnit || '').trim();
        if (!outUom) {
          setToast({ isOpen: true, message: 'Seleccione la unidad de salida', type: 'error' });
          return;
        }
        payload.output_product_type = outputProductType;
        payload.output_quantity = outQty;
        payload.output_unit_of_measure = outUom;
        if (outputProductType === 'producto_en_proceso') {
          payload.fabricar_subreceta_desde_pt = fabricarSubrecetaDesdePt;
        }
      }
    }

    setSaving(true);
    try {
      let res;
      if (isEdit) {
        res = await recetaPlatoService.update(editId, payload);
      } else {
        res = await recetaPlatoService.create(payload);
      }
      if (res.success) {
        setToast({
          isOpen: true,
          message: isEdit ? 'Receta actualizada' : 'Receta guardada',
          type: 'success',
        });
        setTimeout(() => navigate(listPath), 600);
      } else {
        setToast({
          isOpen: true,
          message: res.message || 'No se pudo guardar',
          type: 'error',
        });
      }
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        (err.response?.data?.errors && Object.values(err.response.data.errors).flat().join(' ')) ||
        err.message ||
        'Error al guardar';
      setToast({ isOpen: true, message: msg, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loadingClasificacion || (isEdit && loadingRecipe)) {
    return (
      <div className="p-6 max-w-7xl mx-auto flex justify-center items-center min-h-[16rem]">
        <p className="text-gray-600">{loadingClasificacion ? 'Cargando clasificación…' : 'Cargando receta…'}</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto w-full">
      <nav className="text-sm text-gray-600 mb-4">
        <Link to="/dashboard" className="hover:text-teal-600">
          Dashboard
        </Link>
        <span className="mx-2">/</span>
        <Link to={moduleBase} className="hover:text-teal-600">
          {moduleName}
        </Link>
        <span className="mx-2">/</span>
        <Link to={listPath} className="hover:text-teal-600">
          Recetas
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-800">{isEdit ? 'Editar' : 'Nueva'}</span>
      </nav>

      <h1 className="text-2xl font-bold text-gray-800">{isEdit ? 'Editar receta' : 'Nueva receta'}</h1>
      <p className="text-gray-600 text-sm mt-1 mb-6 max-w-4xl">
        Clasificación de recetas y <strong>productos insumo</strong> (solo los marcados como insumo en el módulo de
        productos).
      </p>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-lg shadow border border-gray-200 p-5 sm:p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Datos generales</h2>
          <div className="grid gap-4 lg:grid-cols-12 lg:gap-6">
            <div className="lg:col-span-12">
              <label htmlFor="receta-nombre" className="block text-sm font-medium text-gray-700 mb-1">
                Nombre de la receta <span className="text-red-500">*</span>
              </label>
              <input
                id="receta-nombre"
                type="text"
                value={name}
                onChange={(e) => {
                  const v = e.target.value;
                  setName(v);
                  if (showOutputConfig && multiOutputEnabled) {
                    setOutputs((prev) =>
                      prev.map((o, i) => (i === 0 ? { ...o, name: v } : o)),
                    );
                  }
                }}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder="Ej. Concha vainilla"
                autoComplete="off"
              />
              {showOutputConfig && multiOutputEnabled ? (
                <p className="mt-1 text-xs text-gray-500">
                  El nombre de la receta actualiza el producto primario de salida en el catálogo al guardar.
                </p>
              ) : showOutputConfig ? (
                <p className="mt-1 text-xs text-gray-500">
                  Al guardar, el producto vinculado en catálogo tomará este mismo nombre.
                </p>
              ) : null}
            </div>

            <div className="lg:col-span-4">
              <label htmlFor="receta-categoria" className="block text-sm font-medium text-gray-700 mb-1">
                Categoría <span className="text-red-500">*</span>
              </label>
              <select
                id="receta-categoria"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white"
              >
                <option value="">Seleccione…</option>
                {hierarchy.filter((c) => c.is_active).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="lg:col-span-4">
              <label htmlFor="receta-sub" className="block text-sm font-medium text-gray-700 mb-1">
                Subcategoría <span className="text-red-500">*</span>
              </label>
              <select
                id="receta-sub"
                value={subcategoryId}
                onChange={(e) => setSubcategoryId(e.target.value)}
                disabled={!categoryId}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white disabled:bg-gray-100 disabled:text-gray-500"
              >
                <option value="">Seleccione…</option>
                {subcategories.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="lg:col-span-4">
              <label htmlFor="receta-familia" className="block text-sm font-medium text-gray-700 mb-1">
                Familia <span className="text-red-500">*</span>
              </label>
              <select
                id="receta-familia"
                value={familyId}
                onChange={(e) => setFamilyId(e.target.value)}
                disabled={!subcategoryId}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white disabled:bg-gray-100 disabled:text-gray-500"
              >
                <option value="">Seleccione…</option>
                {families.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </div>

            {showLineaField && (
              <div className="lg:col-span-4">
                <label htmlFor="receta-linea" className="block text-sm font-medium text-gray-700 mb-1">
                  Línea de producción
                </label>
                <select
                  id="receta-linea"
                  value={produccionLineaId}
                  onChange={(e) => setProduccionLineaId(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white"
                >
                  <option value="">Sin línea</option>
                  {lineas.map((linea) => (
                    <option key={linea.id} value={linea.id}>
                      {linea.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="lg:col-span-12">
              <div className="rounded-md bg-gray-50 border border-gray-100 px-3 py-2 text-sm">
                <span className="text-gray-500">Clasificación: </span>
                <span className="text-gray-800 font-medium">{classificationPreview}</span>
              </div>
            </div>

            <div className="lg:col-span-12 flex items-center gap-3">
              <input
                id="receta-activo"
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
              />
              <label htmlFor="receta-activo" className="text-sm font-medium text-gray-700">
                Receta activa
              </label>
            </div>
          </div>
        </div>

        {showOutputConfig && (
          <div className="bg-white rounded-lg shadow border border-gray-200 p-5 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-1">Configuración de salida</h2>
            <p className="text-sm text-gray-500 mb-4">
              Define qué produce esta receta al ejecutarse. Puede generar un solo producto o{' '}
              <strong>varios a la vez</strong> (co-productos) en el mismo lote.
            </p>

            <label className="flex items-start gap-3 mb-5 cursor-pointer rounded-lg border border-gray-200 px-4 py-3 hover:border-teal-300">
              <input
                type="checkbox"
                checked={multiOutputEnabled}
                onChange={(e) => {
                  const on = e.target.checked;
                  setMultiOutputEnabled(on);
                  if (on) {
                    setOutputs((prev) => {
                      if (prev.length >= 2) return prev;
                      return [
                        {
                          id: null,
                          localId: newLineId(),
                          name: name.trim() || '',
                          product_id: null,
                          output_product_type: outputProductType,
                          output_quantity: outputQuantity || RECETA_OUTPUT_DEFAULTS.output_quantity,
                          output_unit_of_measure: outputUnit || RECETA_OUTPUT_DEFAULTS.output_unit_of_measure,
                          cost_share_pct: '50',
                        },
                        {
                          id: null,
                          localId: newLineId(),
                          name: '',
                          product_id: null,
                          output_product_type: RECETA_OUTPUT_DEFAULTS.output_product_type,
                          output_quantity: RECETA_OUTPUT_DEFAULTS.output_quantity,
                          output_unit_of_measure: RECETA_OUTPUT_DEFAULTS.output_unit_of_measure,
                          cost_share_pct: '50',
                        },
                      ];
                    });
                  }
                }}
                className="mt-1 h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
              />
              <span>
                <span className="block text-sm font-medium text-gray-800">
                  Generar dos o más productos a la vez
                </span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  Al sacar a producción, la guía de entrada registrará todos los productos del lote. La
                  cantidad elaborada de la guía se interpreta sobre el <strong>primer producto</strong>{' '}
                  (referencia de escala).
                </span>
              </span>
            </label>

            {!multiOutputEnabled ? (
              <div className="grid gap-4 lg:grid-cols-12 lg:gap-6">
                <div className="lg:col-span-12">
                  <span className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de producto <span className="text-red-500">*</span>
                  </span>
                  <div className="flex flex-col sm:flex-row gap-4">
                    {recetaOutputTypesForPrimary().map((opt) => (
                      <label
                        key={opt.value}
                        className={`flex items-start gap-2 cursor-pointer rounded-lg border px-4 py-3 flex-1 ${
                          outputProductType === opt.value
                            ? 'border-teal-500 bg-teal-50/50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="output-product-type"
                          value={opt.value}
                          checked={outputProductType === opt.value}
                          onChange={() => setOutputProductType(opt.value)}
                          className="mt-0.5 h-4 w-4 text-teal-600 focus:ring-teal-500"
                        />
                        <span>
                          <span className="block text-sm font-medium text-gray-800">{opt.label}</span>
                          <span className="block text-xs text-gray-500 mt-0.5">
                            {opt.value === 'producto_terminado'
                              ? 'Sale a la venta como producto final.'
                              : 'Se usa como insumo en otras elaboraciones.'}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {outputProductType === 'producto_en_proceso' && (
                  <div className="lg:col-span-12 mt-2">
                    <span className="block text-sm font-medium text-gray-700 mb-2">
                      Uso al elaborar producto terminado
                    </span>
                    <div className="flex flex-col gap-3">
                      {RECETA_PEP_USO_EN_PT_OPTIONS.map((opt) => (
                        <label
                          key={opt.label}
                          className={`flex items-start gap-2 cursor-pointer rounded-lg border px-4 py-3 ${
                            fabricarSubrecetaDesdePt === opt.value
                              ? 'border-teal-500 bg-teal-50/50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="pep-uso-en-pt"
                            checked={fabricarSubrecetaDesdePt === opt.value}
                            onChange={() => setFabricarSubrecetaDesdePt(opt.value)}
                            className="mt-0.5 h-4 w-4 text-teal-600 focus:ring-teal-500"
                          />
                          <span>
                            <span className="block text-sm font-medium text-gray-800">{opt.label}</span>
                            <span className="block text-xs text-gray-500 mt-0.5">{opt.description}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="lg:col-span-4">
                  <label htmlFor="receta-salida-cantidad" className="block text-sm font-medium text-gray-700 mb-1">
                    Cantidad de salida <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="receta-salida-cantidad"
                    type="text"
                    inputMode="decimal"
                    value={outputQuantity}
                    onChange={(e) => setOutputQuantity(sanitizeQuantityInput(e.target.value))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono tabular-nums focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="1"
                    autoComplete="off"
                  />
                </div>

                <div className="lg:col-span-4">
                  <label htmlFor="receta-salida-unidad" className="block text-sm font-medium text-gray-700 mb-1">
                    Unidad de salida <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="receta-salida-unidad"
                    value={outputUnit}
                    onChange={(e) => setOutputUnit(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white"
                  >
                    {outputUnitsOptions.map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <p className="text-sm text-gray-600">
                    Productos del lote. El primero es la <strong>referencia</strong> para escalar insumos.
                  </p>
                  <button
                    type="button"
                    onClick={() =>
                      setOutputs((prev) => [
                        ...prev,
                        {
                          id: null,
                          localId: newLineId(),
                          name: '',
                          product_id: null,
                          output_product_type: RECETA_OUTPUT_SALIDA_INSUMO,
                          output_quantity: RECETA_OUTPUT_DEFAULTS.output_quantity,
                          output_unit_of_measure: outputUnit || RECETA_OUTPUT_DEFAULTS.output_unit_of_measure,
                          cost_share_pct: '',
                        },
                      ])
                    }
                    className="shrink-0 px-3 py-2 border border-teal-600 text-teal-700 rounded-md text-sm font-medium hover:bg-teal-50"
                  >
                    + Agregar producto
                  </button>
                </div>

                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-gray-700">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Producto salida</th>
                        <th className="text-left px-3 py-2 font-medium w-36">Tipo</th>
                        <th className="text-left px-3 py-2 font-medium w-28">Cantidad</th>
                        <th className="text-left px-3 py-2 font-medium w-28">Unidad</th>
                        <th className="text-left px-3 py-2 font-medium w-24">% costo</th>
                        <th className="w-12 px-2 py-2" aria-hidden />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {outputs.map((out, idx) => (
                        <tr key={out.localId} className="hover:bg-gray-50/80">
                          <td className="px-3 py-2 align-middle relative">
                            <div className="flex flex-col gap-1 min-w-[12rem]">
                              {idx === 0 ? (
                                <span className="text-[0.65rem] uppercase tracking-wide text-teal-700 font-semibold">
                                  Primario (escala)
                                </span>
                              ) : null}
                              <ProductoBuscador
                                mode="salida"
                                value={out.name}
                                productId={out.product_id}
                                onSelectProduct={({ productId, label, unit_of_measure }) => {
                                  setOutputs((prev) =>
                                    prev.map((o) => {
                                      if (o.localId !== out.localId) return o;
                                      const patch = {
                                        product_id: productId,
                                        name: label || '',
                                      };
                                      if (productId != null && unit_of_measure) {
                                        patch.output_unit_of_measure = pickOutputUnit(unit_of_measure);
                                      }
                                      return { ...o, ...patch };
                                    }),
                                  );
                                }}
                                placeholder={
                                  idx === 0
                                    ? name.trim() || 'Buscar producto por nombre o SKU…'
                                    : 'Buscar producto por nombre o SKU…'
                                }
                              />
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={out.output_product_type}
                              onChange={(e) =>
                                setOutputs((prev) =>
                                  prev.map((o) =>
                                    o.localId === out.localId
                                      ? { ...o, output_product_type: e.target.value }
                                      : o,
                                  ),
                                )
                              }
                              className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white"
                            >
                              {(idx === 0 ? recetaOutputTypesForPrimary() : recetaOutputTypesForCoProduct()).map(
                                (opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ),
                              )}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              inputMode="decimal"
                              value={out.output_quantity}
                              onChange={(e) =>
                                setOutputs((prev) =>
                                  prev.map((o) =>
                                    o.localId === out.localId
                                      ? { ...o, output_quantity: sanitizeQuantityInput(e.target.value) }
                                      : o,
                                  ),
                                )
                              }
                              className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm font-mono"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={out.output_unit_of_measure}
                              onChange={(e) =>
                                setOutputs((prev) =>
                                  prev.map((o) =>
                                    o.localId === out.localId
                                      ? { ...o, output_unit_of_measure: e.target.value }
                                      : o,
                                  ),
                                )
                              }
                              className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white"
                            >
                              {outputUnitsOptions.map((u) => (
                                <option key={u} value={u}>
                                  {u}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="text"
                              inputMode="decimal"
                              value={out.cost_share_pct}
                              onChange={(e) =>
                                setOutputs((prev) =>
                                  prev.map((o) =>
                                    o.localId === out.localId
                                      ? { ...o, cost_share_pct: sanitizeQuantityInput(e.target.value) }
                                      : o,
                                  ),
                                )
                              }
                              placeholder="auto"
                              className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm font-mono"
                              title="Vacío = repartir en partes iguales"
                            />
                          </td>
                          <td className="px-2 py-2 text-center">
                            {outputs.length > 2 ? (
                              <button
                                type="button"
                                onClick={() =>
                                  setOutputs((prev) => prev.filter((o) => o.localId !== out.localId))
                                }
                                className="text-red-600 hover:text-red-800 text-lg leading-none px-1"
                                aria-label="Quitar salida"
                              >
                                ×
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="bg-white rounded-lg shadow border border-gray-200 p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Productos / ingredientes</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Productos <strong>insumo</strong>
                {showOutputConfig ? (
                  <>
                    {' '}
                    y <strong>en proceso</strong> (salida de otras recetas de producción). La cantidad es{' '}
                    <strong>neta</strong>; el % merma calcula el bruto a despachar. Opcionalmente puede
                    recuperar un <strong>subproducto</strong> del insumo (ej. pepas de papaya = % del bruto).
                  </>
                ) : (
                  <> marcados como insumo.</>
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={addLine}
              className="shrink-0 px-3 py-2 border border-teal-600 text-teal-700 rounded-md text-sm font-medium hover:bg-teal-50 transition-colors"
            >
              + Agregar fila
            </button>
          </div>

          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="text-left px-3 py-2 font-medium min-w-[12rem]">Producto / ingrediente</th>
                  <th className="text-left px-3 py-2 font-medium w-32">
                    <span className="block">Cant. neta</span>
                    <span className="block text-[0.7rem] font-normal text-gray-500 normal-case">(hasta 8 decimales)</span>
                  </th>
                  {showOutputConfig ? (
                    <>
                      <th className="text-left px-3 py-2 font-medium w-24">
                        <span className="block">% merma</span>
                        <span className="block text-[0.7rem] font-normal text-gray-500 normal-case">planificada</span>
                      </th>
                      <th className="text-left px-3 py-2 font-medium w-28">
                        <span className="block">Bruto</span>
                        <span className="block text-[0.7rem] font-normal text-gray-500 normal-case">a despachar</span>
                      </th>
                    </>
                  ) : null}
                  <th className="text-left px-3 py-2 font-medium w-28">
                    <span className="block">Unidad</span>
                    <span className="block text-[0.7rem] font-normal text-gray-500 normal-case">(del producto)</span>
                  </th>
                  <th className="w-12 px-2 py-2" aria-hidden />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lines.map((line) => {
                  const netRaw = parseFloat(String(line.quantity || '').replace(',', '.'));
                  const mermaRaw = parseFloat(String(line.merma_pct || '0').replace(',', '.'));
                  const net = Number.isFinite(netRaw) && netRaw > 0 ? netRaw : 0;
                  const merma = Number.isFinite(mermaRaw) && mermaRaw > 0 ? mermaRaw : 0;
                  const bruto =
                    net > 0
                      ? Math.round(net * (1 + merma / 100) * RECETA_QTY_DECIMAL_FACTOR) / RECETA_QTY_DECIMAL_FACTOR
                      : null;
                  return (
                    <React.Fragment key={line.id}>
                    <tr className="hover:bg-gray-50/80">
                      <td className="px-3 py-2 align-middle relative">
                        <ProductoBuscador
                          mode="insumo"
                          insumoCatalog={insumoProducts}
                          value={line.product_label}
                          productId={line.product_id}
                          onSelectProduct={({ productId, label, unit_of_measure }) => {
                            const patch = { product_id: productId, product_label: label || '' };
                            if (productId != null) {
                              let uom = unit_of_measure;
                              if ((uom == null || String(uom).trim() === '') && Array.isArray(insumoProducts)) {
                                const p = insumoProducts.find((x) => String(x.id) === String(productId));
                                uom = p?.unit_of_measure;
                              }
                              patch.unit = resolveRecipeIngredientUnitDisplay(uom, ingredientUnits);
                            } else if (!String(label || '').trim()) {
                              patch.unit = '';
                            }
                            updateLine(line.id, patch);
                          }}
                          placeholder="Buscar insumo por nombre o SKU…"
                        />
                      </td>
                      <td className="px-3 py-2 align-middle">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={line.quantity}
                          onChange={(e) =>
                            updateLine(line.id, { quantity: sanitizeQuantityInput(e.target.value) })
                          }
                          className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm font-mono tabular-nums focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                          placeholder="0.00000000"
                          title="Cantidad neta de la fórmula (sin merma)"
                          autoComplete="off"
                          spellCheck={false}
                        />
                      </td>
                      {showOutputConfig ? (
                        <>
                          <td className="px-3 py-2 align-middle">
                            <input
                              type="text"
                              inputMode="decimal"
                              value={line.merma_pct}
                              onChange={(e) =>
                                updateLine(line.id, { merma_pct: sanitizeQuantityInput(e.target.value) })
                              }
                              className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm font-mono tabular-nums focus:ring-1 focus:ring-teal-500"
                              placeholder="0"
                              title="Merma % sobre la cantidad neta. Bruto = neto × (1 + merma/100)"
                              autoComplete="off"
                            />
                          </td>
                          <td className="px-3 py-2 align-middle">
                            <div
                              className="w-full min-h-[2.25rem] border border-amber-100 rounded px-2 py-1.5 text-sm bg-amber-50/60 text-amber-900 flex items-center font-mono tabular-nums"
                              title="Cantidad bruta que saldrá de almacén"
                            >
                              {bruto != null ? bruto : <span className="text-gray-400">—</span>}
                            </div>
                          </td>
                        </>
                      ) : null}
                      <td className="px-3 py-2 align-middle">
                        <div
                          className="w-full min-h-[2.25rem] border border-gray-100 rounded px-2 py-1.5 text-sm bg-gray-50 text-gray-800 flex items-center"
                          title="Unidad de medida del producto (no editable)"
                        >
                          {line.product_id && line.unit ? (
                            <span className="font-mono font-medium">{line.unit}</span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-2 align-middle text-center">
                        <button
                          type="button"
                          onClick={() => removeLine(line.id)}
                          className="text-gray-400 hover:text-red-600 p-1 rounded"
                          title="Quitar fila"
                          aria-label="Quitar fila"
                        >
                          <span className="text-lg leading-none">×</span>
                        </button>
                      </td>
                    </tr>
                    {showOutputConfig && !line.byproduct_open ? (
                      <tr className="border-b border-gray-50">
                        <td colSpan={6} className="px-3 py-1.5">
                          <button
                            type="button"
                            onClick={() => updateLine(line.id, { byproduct_open: true })}
                            className="text-xs font-medium text-teal-700 hover:text-teal-900 hover:underline"
                          >
                            + Agregar subproducto
                          </button>
                        </td>
                      </tr>
                    ) : null}
                    {showOutputConfig && line.byproduct_open ? (
                      <tr className="bg-slate-50/80 border-b border-gray-100">
                        <td colSpan={6} className="px-3 py-2">
                          <div className="flex flex-col lg:flex-row lg:items-end gap-3">
                            <div className="flex-1 min-w-[14rem]">
                              <label className="block text-[0.7rem] font-medium text-slate-600 mb-1">
                                Subproducto recuperable
                              </label>
                              <ProductoBuscador
                                mode="insumo"
                                insumoCatalog={insumoProducts}
                                value={line.byproduct_label}
                                productId={line.byproduct_product_id}
                                onSelectProduct={({ productId, label, unit_of_measure }) => {
                                  const patch = {
                                    byproduct_product_id: productId,
                                    byproduct_label: label || '',
                                  };
                                  if (productId != null) {
                                    let uom = unit_of_measure;
                                    if ((uom == null || String(uom).trim() === '') && Array.isArray(insumoProducts)) {
                                      const p = insumoProducts.find((x) => String(x.id) === String(productId));
                                      uom = p?.unit_of_measure;
                                    }
                                    patch.byproduct_unit = resolveRecipeIngredientUnitDisplay(
                                      uom,
                                      ingredientUnits,
                                    );
                                  } else {
                                    patch.byproduct_unit = '';
                                    if (!String(label || '').trim()) {
                                      patch.byproduct_pct = '0';
                                    }
                                  }
                                  updateLine(line.id, patch);
                                }}
                                placeholder="Ej. pepas de papaya…"
                              />
                            </div>
                            <div className="w-28">
                              <label className="block text-[0.7rem] font-medium text-slate-600 mb-1">
                                % del bruto
                              </label>
                              <input
                                type="text"
                                inputMode="decimal"
                                value={line.byproduct_pct}
                                onChange={(e) =>
                                  updateLine(line.id, {
                                    byproduct_pct: sanitizeQuantityInput(e.target.value),
                                  })
                                }
                                className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm font-mono"
                                placeholder="0"
                                title="% del bruto del insumo que se recupera como subproducto"
                              />
                            </div>
                            <div className="w-36 text-xs text-slate-600 pb-1.5">
                              {(() => {
                                const bp = parseFloat(String(line.byproduct_pct || '0').replace(',', '.'));
                                if (!(bruto > 0) || !(bp > 0) || !line.byproduct_product_id) {
                                  return <span className="text-gray-400">Recupera: —</span>;
                                }
                                const recovered =
                                  Math.round(bruto * (bp / 100) * RECETA_QTY_DECIMAL_FACTOR) /
                                  RECETA_QTY_DECIMAL_FACTOR;
                                return (
                                  <span className="font-mono text-teal-800">
                                    Recupera: {recovered} {line.byproduct_unit || line.unit || ''}
                                  </span>
                                );
                              })()}
                            </div>
                            <div className="pb-1">
                              <button
                                type="button"
                                onClick={() =>
                                  updateLine(line.id, {
                                    byproduct_open: false,
                                    byproduct_product_id: null,
                                    byproduct_label: '',
                                    byproduct_pct: '0',
                                    byproduct_unit: '',
                                  })
                                }
                                className="text-xs text-gray-500 hover:text-red-600"
                                title="Quitar subproducto"
                              >
                                Quitar
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 pt-2">
          <button
            type="submit"
            disabled={saving}
            className="px-5 py-2.5 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-md text-sm font-medium transition-colors"
          >
            {saving ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Guardar receta'}
          </button>
          <Link
            to={listPath}
            className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors inline-flex items-center"
          >
            Cancelar
          </Link>
        </div>
      </form>

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, isOpen: false }))}
      />
    </div>
  );
};

export default RecetasForm;
